"use client"

import { AlertCircle, Check, LoaderCircle, MessageSquareText, X } from "lucide-react"
import type { ApprovalRecord } from "@resolto/domain"
import { useState } from "react"
import { StatusBadge } from "./status-badge"

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(new Date(value))
}

export function ApprovalDecision({ initialApproval }: { initialApproval: ApprovalRecord }) {
  const [approval, setApproval] = useState(initialApproval)
  const [decision, setDecision] = useState<"approved" | "rejected" | "clarification_requested">(
    "approved",
  )
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  async function submitDecision() {
    setSubmitting(true)
    setError("")
    try {
      const response = await fetch(`/api/demo/approvals/${approval.id}/decision`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": crypto.randomUUID(),
        },
        body: JSON.stringify({ decision, reason }),
      })
      const payload = (await response.json()) as ApprovalRecord & {
        error?: { message?: string }
      }
      if (!response.ok) {
        throw new Error(payload.error?.message ?? "The decision was not recorded.")
      }
      setApproval(payload)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The decision was not recorded.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <article className="approval-card" id={approval.id}>
      <header>
        <div>
          <span>{approval.caseReference}</span>
          <h2>{approval.title}</h2>
          <p>
            {approval.action} · requested {formatTimestamp(approval.requestedAt)} UTC
          </p>
        </div>
        <div className="approval-badges">
          <StatusBadge status={approval.status} />
          <StatusBadge
            status={approval.risk === "moderate" ? "high" : "normal"}
            label={`${approval.risk} risk`}
          />
        </div>
      </header>
      <div className="approval-preview">
        <div>
          <span>Exact target</span>
          <strong>{approval.target}</strong>
        </div>
        <div className="change-preview">
          <section>
            <span>Current state</span>
            <p>{approval.currentValue}</p>
          </section>
          <section>
            <span>Proposed state</span>
            <p>{approval.proposedValue}</p>
          </section>
        </div>
        <div className="approval-effect">
          <Check aria-hidden="true" />
          <p>
            <strong>Expected effect and verification</strong>
            {approval.expectedEffect}
          </p>
        </div>
        <dl>
          <div>
            <dt>Required authority</dt>
            <dd>{approval.requiredRole.replaceAll("_", " ")}</dd>
          </div>
          <div>
            <dt>Evidence</dt>
            <dd>{approval.evidenceIds.length} cited records</dd>
          </div>
          <div>
            <dt>Rollback</dt>
            <dd>
              {approval.risk === "moderate" ? "Compensating action available" : "Safe retry only"}
            </dd>
          </div>
          <div>
            <dt>Optimistic UI</dt>
            <dd>Disabled for governed actions</dd>
          </div>
        </dl>
      </div>

      {approval.status === "waiting" ? (
        <div className="approval-decision-form">
          {error && (
            <div className="form-error" role="alert">
              <AlertCircle aria-hidden="true" />
              <span>{error} No action was executed.</span>
            </div>
          )}
          <fieldset>
            <legend>Decision</legend>
            <label>
              <input
                type="radio"
                name={`decision-${approval.id}`}
                checked={decision === "approved"}
                onChange={() => setDecision("approved")}
              />
              <Check aria-hidden="true" />
              Approve
            </label>
            <label>
              <input
                type="radio"
                name={`decision-${approval.id}`}
                checked={decision === "clarification_requested"}
                onChange={() => setDecision("clarification_requested")}
              />
              <MessageSquareText aria-hidden="true" />
              Request clarification
            </label>
            <label>
              <input
                type="radio"
                name={`decision-${approval.id}`}
                checked={decision === "rejected"}
                onChange={() => setDecision("rejected")}
              />
              <X aria-hidden="true" />
              Reject
            </label>
          </fieldset>
          <label>
            Decision reason
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              minLength={3}
              maxLength={1_000}
              placeholder={
                decision === "approved"
                  ? "Confirm why the target, evidence, and effect are acceptable."
                  : "Explain what must change or what information is missing."
              }
            />
          </label>
          <button
            type="button"
            className={`button ${decision === "approved" ? "button-primary" : "button-secondary"}`}
            disabled={submitting || reason.trim().length < 3}
            onClick={() => void submitDecision()}
          >
            {submitting ? <LoaderCircle className="spin" aria-hidden="true" /> : null}
            {submitting ? "Recording decision…" : "Record decision"}
          </button>
          <p>
            Approval records the decision only. The action remains locked until server-side
            preconditions and permissions are re-checked.
          </p>
        </div>
      ) : (
        <div className={`decision-record decision-${approval.status}`} role="status">
          <StatusBadge status={approval.status} />
          <div>
            <strong>Decision recorded</strong>
            <p>{approval.decision?.reason}</p>
            <p>
              The requested action has not run. Execution remains subject to fresh server-side
              permission and precondition checks.
            </p>
            <span>
              {approval.decision?.actor} ·{" "}
              {approval.decision ? `${formatTimestamp(approval.decision.decidedAt)} UTC` : ""}
            </span>
          </div>
        </div>
      )}
    </article>
  )
}
